import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../../api/client';
import type { ProcrastinationSuggestion } from '../../../api/types';
import { useChangeTaskStatus } from '../../tasks/hooks/useChangeTaskStatus';
import { isTaskNotFound } from '../../tasks/lib/updateTaskError';
import { EmptyStateMessage } from '../components/EmptyStateMessage';
import { EnergySlider } from '../components/EnergySlider';
import { SuggestedTaskCard } from '../components/SuggestedTaskCard';
import { useDismissSuggestion } from '../hooks/useDismissSuggestion';
import { useSuggestTask } from '../hooks/useSuggestTask';
import styles from './ProcrastinationScreen.module.css';

interface ProcrastinationScreenProps {
  onClose: () => void;
}

type Phase = 'energy' | 'suggested' | 'empty';

// PARA-105, AC-3/AC-E2 — режим прокрастинации: energy input → suggested/empty.
// PARA-139 (H-1, AC-2/AC-3/AC-4) — «Done» ждёт результат мутации: onClose
// только в onSuccess, при ошибке экран остаётся и показывает сообщение,
// кнопка заблокирована через isPending. Ошибка хранится локально (useState) и
// сбрасывается при новом тапе / «Другую» — 404 мапится отдельным текстом по
// аналогии с BacklogScreen (isTaskNotFound).
export function ProcrastinationScreen({ onClose }: ProcrastinationScreenProps): JSX.Element {
  const { t } = useTranslation('procrastination');
  const [energy, setEnergy] = useState(3);
  const [phase, setPhase] = useState<Phase>('energy');
  const [suggestion, setSuggestion] = useState<ProcrastinationSuggestion | null>(null);
  const [completeError, setCompleteError] = useState<unknown>(null);
  const { mutate: suggest } = useSuggestTask();
  const { mutate: dismiss } = useDismissSuggestion();
  const { mutate: changeStatus, isPending: isCompleting } = useChangeTaskStatus();

  const completeErrorMessage =
    completeError === null
      ? null
      : isTaskNotFound(completeError)
        ? t('suggested.completeNotFound')
        : t('suggested.completeError');

  const requestSuggestion = (excludeTaskId?: number): void => {
    suggest(
      { energy, excludeTaskId },
      {
        onSuccess: (result) => {
          setSuggestion(result);
          setPhase('suggested');
        },
        onError: (error) => {
          if (error instanceof ApiError && error.status === 404) {
            setSuggestion(null);
            setPhase('empty');
          }
        },
      },
    );
  };

  if (phase === 'empty') {
    return (
      <div className={styles.centered}>
        <EmptyStateMessage onChangeEnergy={() => setPhase('energy')} />
      </div>
    );
  }

  if (phase === 'suggested' && suggestion !== null) {
    return (
      <div className={styles.screen}>
        <SuggestedTaskCard
          suggestion={suggestion}
          isPending={isCompleting}
          errorMessage={completeErrorMessage}
          onComplete={() => {
            setCompleteError(null);
            // no-console разрешает только warn/error (eslint no-console), поэтому debug-лог — через console.warn.
            console.warn('[PARA-139] complete: start', { taskId: suggestion.id, status: 'DONE' });
            changeStatus(
              { taskId: suggestion.id, status: 'DONE' },
              {
                onSuccess: () => {
                  console.warn('[PARA-139] complete: success', { taskId: suggestion.id });
                  onClose();
                },
                onError: (error) => {
                  console.warn('[PARA-139] complete: error', { taskId: suggestion.id, error });
                  setCompleteError(error);
                },
              },
            );
          }}
          onAnother={() => {
            setCompleteError(null);
            requestSuggestion(suggestion.id);
          }}
          onDismiss={() => {
            dismiss(suggestion.session_id);
            onClose();
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <EnergySlider value={energy} onChange={setEnergy} onSubmit={() => requestSuggestion()} />
    </div>
  );
}
